import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Coins, TrendingUp, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tokens = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTokens();
    const interval = setInterval(fetchTokens, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchTokens = async () => {
    try {
      const response = await axios.get(`${API}/tokens`);
      setTokens(response.data.tokens);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          All Tokens
        </h1>
        <p className="text-text-secondary">View all tokens on the blockchain</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tokens.map((token, idx) => (
          <motion.div
            key={token.symbol}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            data-testid={`token-${token.symbol}`}
            className="glass p-6 rounded-sm hover:border-primary/30 transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                  <Coins size={24} className="text-secondary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-unbounded text-xl font-bold text-text-primary">
                    {token.symbol}
                  </h3>
                  <p className="text-text-muted text-sm">{token.name}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm">Total Supply</span>
                <span className="font-bold text-accent-success text-lg">
                  {token.total_supply.toLocaleString()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm">Decimals</span>
                <span className="font-mono text-text-primary">{token.decimals}</span>
              </div>

              <div>
                <span className="text-text-muted text-sm block mb-1">Creator</span>
                <span className="font-mono text-xs text-text-secondary break-all">
                  {token.creator}
                </span>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-text-muted text-sm">Created</span>
                <span className="text-xs text-text-secondary">
                  {new Date(token.created_at * 1000).toLocaleDateString()}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {tokens.length === 0 && (
        <div className="glass p-12 rounded-sm text-center">
          <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Coins size={32} className="text-secondary" strokeWidth={1.5} />
          </div>
          <p className="text-text-muted">No tokens created yet</p>
        </div>
      )}
    </div>
  );
};

export default Tokens;
